import React, { useState, useRef } from 'react';
import { debounce } from 'lodash';
import { z } from 'zod';
import { Loader, Sparkle, Upload, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { transcriptGenService } from 'features/transcript/services/transcriptGenService';
import {
  isYouTubeUrl,
  validateFileSize,
  validateMediaFile,
} from 'features/transcript/utils/transcriptUtils';

const SelectFilePanel: React.FC = () => {
  const [selectedMedia, setSelectedMedia] = useState<File | string | null>(
    null
  );
  const [mediaName, setMediaName] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const MAX_FILE_SIZE_MB = 20;

  const urlSchema = z.string().url();

  const validateFile = (file: File) => {
    const sizeError = validateFileSize(file, MAX_FILE_SIZE_MB);
    if (sizeError) {
      setSizeError(sizeError);
      return null;
    }

    const typeError = validateMediaFile(file);
    if (typeError) {
      setSizeError(typeError);
      return null;
    }

    setSizeError(null);
    setSelectedMedia(file);
    setMediaName(file.name);
    return file;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateFile(file);
    }
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateFile(file);
    }
  };

  const handleUrlValidation = debounce(async (url: string) => {
    const result = urlSchema.safeParse(url);
    if (result.success) {
      try {
        if (isYouTubeUrl(url)) {
          setUrlError(null);
          setSelectedMedia(url);
          setMediaName('YouTube Video');
          return;
        }

        const urlLower = url.toLowerCase();
        const hasValidExtension = urlLower.match(/\.(mp3|wav|mp4|webm|ogg)$/);

        if (hasValidExtension) {
          setUrlError(null);
          const name = url.split('/').pop() || 'Media';
          setSelectedMedia(url);
          setMediaName(name);
          return;
        }

        const response = await fetch(url);
        const contentType = response.headers.get('content-type');

        if (
          contentType &&
          (contentType.startsWith('video/') || contentType.startsWith('audio/'))
        ) {
          setUrlError(null);
          const name = url.split('/').pop() || 'Media';
          setSelectedMedia(url);
          setMediaName(name);
        } else {
          setUrlError('URL must point to a valid video or audio file');
        }
      } catch (error) {
        console.log('error:', error);
        setUrlError('Failed to fetch the media. Please enter a valid URL');
      }
    } else {
      setUrlError('Please enter a valid URL');
    }
  }, 500);

  const clearSelectedMedia = () => {
    setSelectedMedia(null);
    setMediaName('');
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    try {
      let response;
      if (selectedMedia) {
        if (typeof selectedMedia === 'string') {
          response = await transcriptGenService.uploadFile({
            url: selectedMedia,
          });
        } else {
          response = await transcriptGenService.uploadFile({
            file: selectedMedia,
          });
        }
        navigate('/transcript/generated', { state: { data: response } });
        console.log('Transcript generated:', response);
      }
    } catch (error) {
      console.error('Error generating transcript:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderPreview = () => {
    if (!selectedMedia) return null;

    if (typeof selectedMedia === 'string') {
      if (isYouTubeUrl(selectedMedia)) {
        return (
          <div className="w-full max-h-64 rounded-lg mb-4">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${selectedMedia.split('v=')[1]}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="YouTube Video"
            ></iframe>
          </div>
        );
      } else if (selectedMedia.match(/\.(mp4|webm)$/i)) {
        return (
          <video controls className="w-full  max-h-64 rounded-lg mb-4">
            <source src={selectedMedia} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        );
      } else if (selectedMedia.match(/\.(mp3|wav|ogg)$/i)) {
        return (
          <audio controls className="w-full  mb-4">
            <source src={selectedMedia} type="audio/mpeg" />
            Your browser does not support the audio tag.
          </audio>
        );
      }
    } else {
      const fileType = selectedMedia.type.split('/')[0];
      const objectUrl = URL.createObjectURL(selectedMedia);

      if (fileType === 'video') {
        return (
          <video controls className="w-full max-h-64 rounded-lg mb-4">
            <source src={objectUrl} type={selectedMedia.type} />
            Your browser does not support the video tag.
          </video>
        );
      } else if (fileType === 'audio') {
        return (
          <audio controls className="w-full mb-4">
            <source src={objectUrl} type={selectedMedia.type} />
            Your browser does not support the audio tag.
          </audio>
        );
      }
    }
  };

  return (
    <div className="w-full bg-background rounded-lg max-md:p-4 max-md:mt-5">
      {selectedMedia ? (
        <div className="text-center">
          {renderPreview()}
          <div className="flex items-center w-full px-4 py-2 border border-secondaryNeutral-6 bg-background-card rounded">
            <p className="text-base font-medium text-primary text-opacity-[62.75%]">
              {mediaName}
            </p>
            <X
              onClick={clearSelectedMedia}
              className="w-6 h-6 ml-auto text-primary cursor-pointer"
            />
          </div>
          <button
            onClick={handleGenerate}
            className="w-full bg-primary text-white rounded-lg h-12 flex items-center justify-center gap-2 mt-9"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Sparkle className="h-5 w-5" />
                <span className="text-lg font-medium">Generate Transcript</span>
              </>
            )}
          </button>
        </div>
      ) : (
        <>
          <div
            className={`flex flex-col items-center justify-center w-full h-44 border rounded-lg px-12 border-dashed text-center relative ${
              isDragging
                ? 'border-primaryAccent-9 bg-primaryAccentAlpha-3'
                : 'border-primaryAccent-9'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              id="fileInput"
              type="file"
              accept="video/*, audio/*"
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={handleFileChange}
            />
            <Upload className="mx-auto h-7 w-7 text-primary" />
            <p className="mt-4 text-text-primary text-base font-medium lg:hidden">
              Tap to upload media from your device
            </p>
            <p className="mt-4 text-base text-text-primary font-medium hidden lg:block">
              Drag and drop or click to choose a file
            </p>
            <p className="mt-1 text-xs font-light text-text-primary">
              (UP TO 20MB)
            </p>
          </div>
          {sizeError && (
            <p className="text-red-600 text-xs mt-2">{sizeError}</p>
          )}
          <div className="text-primary flex justify-center items-center mt-[20px] ">
            <div className="border-t border-primary w-full"></div>
            <span className="text-primary font-medium mx-3">or</span>
            <div className="border-t border-primary w-full"></div>
          </div>
          <div className="text-text-primary">
            <h3 className="text-center text-base font-medium mt-4">
              Enter media URL
            </h3>
            <input
              type="text"
              className={`w-full mt-2 px-2 border bg-background  border-opacity-[12.55%] rounded-md placeholder-text-placeholder outline-none ${
                urlError ? 'border-red-600' : ''
              }`}
              placeholder="Insert URL"
              onChange={(e) => handleUrlValidation(e.target.value)}
            />
            {urlError && (
              <p className="text-red-600 text-xs mt-2">{urlError}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SelectFilePanel;
